import * as React from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { Box, Typography, TextField, CircularProgress } from "@mui/material";

function not(a, b) {
    return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
    return [...a, ...not(b, a)];
}

export default function TransferList(field, methods, f, status) {
    const fieldConfig = {
        isOptionEqual: (option, selected) => option === selected,
        getOptionLabel: (option) => option,
        ...field,
    };
    const { setValue } = methods;

    const options = React.useMemo(() => {
        let selected = f.value || [];
        let options = fieldConfig.options || [];
        return options.filter(
            (option) =>
                selected.findIndex((el) =>
                    fieldConfig.isOptionEqual(option, el)
                ) === -1
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldConfig.options, f.value]);

    const [checked, setChecked] = React.useState([]);
    const [left, setLeft] = React.useState([]);
    const [right, setRight] = React.useState([]);
    const [valueFilterLeft, setValueFilterLeft] = React.useState("");
    const [valueFilterRight, setValueFilterRight] = React.useState("");

    React.useEffect(() => {
        if (f.value && Array.isArray(f.value)) {
            setRight(f.value);
        }
    }, [f.value]);

    React.useEffect(() => {
        if (status === "success" && Array.isArray(options)) {
            setLeft(options);
        }
    }, [options, status]);

    React.useEffect(() => {
        setValueFilterLeft("");
        setValueFilterRight("");
    }, [left, right]);

    const filterLeft = React.useMemo(() => {
        if (!valueFilterLeft) return left;
        return left.filter((item) =>
            fieldConfig
                .getOptionLabel(item)
                .toLowerCase()
                .includes(valueFilterLeft.toLowerCase())
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueFilterLeft, left]);

    const filterRight = React.useMemo(() => {
        if (!valueFilterRight) return right;
        return right.filter((item) =>
            fieldConfig
                .getOptionLabel(item)
                .toLowerCase()
                .includes(valueFilterRight.toLowerCase())
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueFilterRight, right]);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const numberOfChecked = (items) => intersection(checked, items).length;

    const handleToggleAll = (items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    const handleCheckedRight = () => {
        const rightData = right.concat(leftChecked);
        setValue(fieldConfig.attribute, rightData);
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        const rightData = not(right, rightChecked);
        setValue(fieldConfig.attribute, rightData);
        setChecked(not(checked, rightChecked));
    };

    const customList = (title, items) => {
        return (
            <Card>
                <TextField
                    type="string"
                    InputProps={{
                        placeholder: "Search",
                        endAdornment: (
                            <>
                                {options.length === 0 &&
                                title === "Choices" &&
                                status !== "error" &&
                                status !== "success" ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                            </>
                        ),
                    }}
                    size="small"
                    sx={{ width: "100%", padding: "8px 8px 0 8px" }}
                    value={
                        title === "Choices" ? valueFilterLeft : valueFilterRight
                    }
                    onChange={(e) =>
                        title === "Choices"
                            ? setValueFilterLeft(e.target.value)
                            : setValueFilterRight(e.target.value)
                    }
                />
                <CardHeader
                    sx={{ px: 2, py: 1 }}
                    avatar={
                        <>
                            <Checkbox
                                onClick={handleToggleAll(items)}
                                checked={
                                    numberOfChecked(items) === items.length &&
                                    items.length !== 0
                                }
                                indeterminate={
                                    numberOfChecked(items) !== items.length &&
                                    numberOfChecked(items) !== 0
                                }
                                disabled={items.length === 0}
                                inputProps={{
                                    "aria-label": "all items selected",
                                }}
                            />{" "}
                        </>
                    }
                    title={title}
                    subheader={`${numberOfChecked(items)}/${
                        items.length
                    } selected`}
                />

                <Divider />
                <List
                    sx={{
                        width: "auto",
                        height: 230,
                        bgcolor: "background.paper",
                        overflow: "auto",
                    }}
                    dense
                    component="div"
                    role="list"
                >
                    {items.map((value, index) => {
                        const label = fieldConfig.getOptionLabel(value);
                        const labelId = `transfer-list-all-item-${label}-label`;

                        return (
                            <ListItem
                                key={index}
                                role="listitem"
                                button
                                onClick={handleToggle(value)}
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        checked={checked.indexOf(value) !== -1}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{
                                            "aria-label": labelId,
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText id={labelId} primary={label} />
                            </ListItem>
                        );
                    })}
                </List>
            </Card>
        );
    };

    return (
        <>
            {fieldConfig.title && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                    }}
                >
                    <Typography>{fieldConfig.title}</Typography>
                </Box>
            )}
            <Grid
                container
                spacing={2}
                justifyContent="left"
                alignItems="center"
            >
                <Grid item flex={1}>
                    {customList("Choices", filterLeft)}
                </Grid>
                <Grid item>
                    <Grid container direction="column" alignItems="center">
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleCheckedRight}
                            disabled={leftChecked.length === 0}
                            aria-label="move selected right"
                        >
                            &gt;
                        </Button>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleCheckedLeft}
                            disabled={rightChecked.length === 0}
                            aria-label="move selected left"
                        >
                            &lt;
                        </Button>
                    </Grid>
                </Grid>
                <Grid item flex={1}>
                    {customList("Chosen", filterRight)}
                </Grid>
            </Grid>
        </>
    );
}
